import { cx } from '@emotion/css';
import MediaPreview from '../MediaPreview/MediaPreview';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { useConversationReadonlyValue } from 'hooks/business/conversation';
import useMediaDrop from '../MediaPreview/useMediaDrop';
import { ChatDialogue, ChatDialogueProps } from './ChatDialogue';
import { useChatContext } from '../../contexts/ChatContext';
import { ChatAlert } from '../ChatAlert';
import { useUnmount } from 'ahooks';
import {
  useCurrentQAPairKnowledgeQuery,
  useQAPairOpenedWithKeepAlive,
  useQAPairVisible,
  useReactiveQAPair,
} from 'hooks/business/im/useQAPair';
import { useQuitQAPair } from 'components/Dialogue/components/QAPair/useQuitQAPair';
import { useMultitabState } from 'views/Multitab/state';
import { useQAPairPersist } from 'components/Dialogue/components/QAPair/useQAPairPersist';
import { useQAPairRecord } from 'hooks/business/im/useQAPairRecord';
import { useAutoCleanKnowledgeQueryListener } from 'hooks/business/im/useQAPairHumanReply';
import { useClearQAPairQueryAIReplyMsgMap } from 'hooks/business/im/useQAPairAIReply';
import { useCleanQAPair } from 'hooks/business/im/useCleanQAPair';
import { ChatMessageEditor } from './ChatMessageEditor';
import { MercadoClaimChat } from './MercadoClaimChat';

interface AdvancedChatProps extends ChatDialogueProps {
  className?: HTMLDivElement['className'];
  conversation: Conversation.Conversation;
  contextMenu: JSX.Element | null;
  showSideFoldBtn?: boolean;
  forceAcquireControlReason?: Conversation.ForceAcquireControlReason;
}

export const AdvancedChat = (props: AdvancedChatProps) => {
  const {
    className,
    conversation,
    contextMenu,
    showSideFoldBtn,
    forceAcquireControlReason,
    isGalaxy,
    ...chatDialogueProps
  } = props;
  const { conversationId, mercadoCapability } = conversation;

  const { highlightMsg, hasQAPairMounted } = useChatContext();
  const readonly = useConversationReadonlyValue();
  const { activeKey } = useMultitabState();
  /**
   * 消息补充知识
   */
  const qaPairVisible = useQAPairVisible();
  const quitQAPair = useQuitQAPair();
  const cleanQAPair = useCleanQAPair();
  const qaPairOpenedWithKeepAlive = useQAPairOpenedWithKeepAlive();
  const reactiveQAPair = useReactiveQAPair();
  useAutoCleanKnowledgeQueryListener(conversationId);
  const qaPairPersist = useQAPairPersist();
  const shouldMountQAPair = !!conversation && !isGalaxy;

  const clearQAPairQueryAIReplyMsgMap = useClearQAPairQueryAIReplyMsgMap();
  const currentQAPairKnowledgeQuery = useCurrentQAPairKnowledgeQuery();

  useQAPairRecord(conversation);

  const dropProps = useMediaDrop(
    !readonly && conversation?.controlStatus ? conversation : undefined,
  );

  const chatAlert = <ChatAlert conversation={conversation} />;

  const hasMercadoClaim = useMemo(() => {
    return mercadoCapability?.results.some(({ resources }) => resources.claimId);
  }, [mercadoCapability]);

  /**
   * QAPair 在进入和离开会话时的处理
   */
  useLayoutEffect(() => {
    if (!currentQAPairKnowledgeQuery) {
      return;
    }

    /**
     * 白名单用户会重新激活
     */
    let timer = null;
    if (shouldMountQAPair) {
      timer = setTimeout(() => {
        reactiveQAPair(conversationId, currentQAPairKnowledgeQuery);
      }, 500);
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, currentQAPairKnowledgeQuery]);

  const cleanQAPairAndQuit = () => {
    cleanQAPair();
    quitQAPair({
      conversation,
      isLeaveConversation: true,
      extra: {
        exit: true,
      },
    });
  };

  useEffect(() => {
    if (!shouldMountQAPair || qaPairOpenedWithKeepAlive) {
      return;
    }
    if (!!activeKey && qaPairVisible && conversation) {
      cleanQAPairAndQuit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // 离开会话时，清除 QAPair
  useUnmount(() => {
    clearQAPairQueryAIReplyMsgMap();

    if (!shouldMountQAPair) {
      return;
    }
    if (qaPairVisible && conversation) {
      cleanQAPairAndQuit();
    }

    if (hasQAPairMounted) {
      qaPairPersist(conversationId);
    }
  });

  return (
    <div
      data-agent-page-exclude=""
      className={cx(
        'flex flex-col overflow-hidden flex-1 h-full bg-[var(--fett-chat-window-bg)]',
        className,
      )}
      {...dropProps}
    >
      {hasMercadoClaim ? (
        <MercadoClaimChat
          chatAlert={chatAlert}
          conversation={conversation}
          contextMenu={contextMenu}
          forceAcquireControlReason={forceAcquireControlReason}
          {...chatDialogueProps}
        />
      ) : (
        <>
          {chatAlert}
          <ChatDialogue
            isGalaxy={isGalaxy}
            conversation={conversation}
            highlightMsg={highlightMsg}
            {...chatDialogueProps}
          />
          <ChatMessageEditor
            conversation={conversation}
            forceAcquireControlReason={forceAcquireControlReason}
            showSideFoldBtn={showSideFoldBtn}
          />
          {contextMenu}
          <MediaPreview />
        </>
      )}
    </div>
  );
};
